import {
  ResolveStyleArgs,
  StyleName,
  StyleVariant,
} from "../../../types/types.customStyles";
import { resolveDynamicFieldStyle } from "../field-sizing";
import { MISMATCHED_FUNCTIONAL_UNIT_NAMES } from "../constants";
import { SETUP_STYLES } from "./setupStyles";

const getResolvedSetupName = (
  setupName: string | undefined,
): string | undefined => {
  if (!setupName) return undefined;

  // Prefer setup keys used by SETUP_STYLES; only fall back to reverse-aliasing
  // when we receive a backend functional unit name.
  if (setupName in SETUP_STYLES) return setupName;

  const internalName = Array.from(MISMATCHED_FUNCTIONAL_UNIT_NAMES.entries()).find(
    ([, backendName]) => backendName === setupName,
  )?.[0];

  return internalName ?? setupName;
};

const getSetupStyleConfig = (setupName: string | undefined) => {
  const resolvedSetupName = getResolvedSetupName(setupName);
  if (!resolvedSetupName) return undefined;
  return SETUP_STYLES[resolvedSetupName];
};

export const resolveStyleToken = (
  token: StyleName | undefined,
  isRecordOpen: boolean,
): string => {
  if (!token) return "";

  if (typeof token === "string") {
    return token;
  }
  const { recordOpened = "", recordClosed = "" } = token;

  return isRecordOpen ? recordOpened : recordClosed;
};

export const isStyleApplicableForSetup = (
  sectionStyle: StyleName | StyleVariant[] | undefined,
  selectedSetup: string | undefined,
  isRecordOpen: boolean,
): string => {
  if (!sectionStyle) return "";

  if (Array.isArray(sectionStyle)) {
    for (const style of sectionStyle) {
      const resolved = isStyleApplicableForSetup(
        style,
        selectedSetup,
        isRecordOpen,
      );
      if (resolved) return resolved;
    }
    return "";
  }

  if (typeof sectionStyle === "string") {
    return sectionStyle;
  }

  const { applicableSetups } = sectionStyle;
  if (!applicableSetups) {
    return resolveStyleToken(sectionStyle, isRecordOpen);
  }

  const normalizedSetup = getResolvedSetupName(selectedSetup);
  if (!normalizedSetup) return "";

  return applicableSetups.includes(normalizedSetup)
    ? resolveStyleToken(sectionStyle, isRecordOpen)
    : "";
};

export const resolveCustomStyle = ({
  applicability,
  sectionKey,
  selectedSetup,
  isHidden = false,
  isRecordOpen = false,
  propertyKey,
  property,
}: ResolveStyleArgs): string => {
  if (isHidden) return "hidden";

  const resolvedSetupName = getResolvedSetupName(selectedSetup);
  const setupStyleConfig = getSetupStyleConfig(selectedSetup);

  switch (applicability) {
    case "record-page-container":
      if (!setupStyleConfig || !resolvedSetupName) return "";
      return resolveStyleToken(
        setupStyleConfig.recordPageContainer?.[resolvedSetupName],
        isRecordOpen,
      );
    case "multi-section":
      if (!setupStyleConfig || !resolvedSetupName) return "";
      return resolveStyleToken(
        setupStyleConfig.multiSections?.[resolvedSetupName],
        isRecordOpen,
      );
    case "section":
      return isStyleApplicableForSetup(
        sectionKey ? setupStyleConfig?.sections?.[sectionKey] : undefined,
        selectedSetup,
        isRecordOpen,
      );
    case "section-title":
      return isStyleApplicableForSetup(
        sectionKey ? setupStyleConfig?.sectionsTitle?.[sectionKey] : undefined,
        selectedSetup,
        isRecordOpen,
      );
    case "section-info-button":
      return isStyleApplicableForSetup(
        sectionKey ? setupStyleConfig?.sectionInfoBtn?.[sectionKey] : undefined,
        selectedSetup,
        isRecordOpen,
      );
    case "section-form":
      return isStyleApplicableForSetup(
        sectionKey ? setupStyleConfig?.sectionForm?.[sectionKey] : undefined,
        selectedSetup,
        isRecordOpen,
      );
    case "field": {
      if (!propertyKey) return "";
      const fieldStyle = setupStyleConfig?.fields?.[propertyKey];
      if (fieldStyle) return fieldStyle as string;

      return resolveDynamicFieldStyle({
        propertyKey,
        property:
          property as Parameters<typeof resolveDynamicFieldStyle>[0]["property"],
      });
    }
    default:
      return "";
  }
};
